import  { useState } from 'react';
import { FileText, Users, DollarSign, TrendingUp, Clock, Plus, BarChart, ArrowUpRight } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SEO } from './shared/SEO';

export function Dashboard() {
  const { invoices } = useApp();
  const { currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('This Month');

  const currency = currentWorkspace?.settings.currency || 'QAR';
  const currencySymbol = getCurrencySymbol(currency);

  const totalRevenue = invoices.reduce((sum, invoice) => sum + invoice.total, 0);
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');
  const pendingInvoices = invoices.filter(inv => inv.status === 'sent');
  const overdueInvoices = invoices.filter(inv => inv.status === 'overdue');
  const paidAmount = paidInvoices.reduce((sum, inv) => sum + inv.total, 0);
  const unpaidAmount = pendingInvoices.reduce((sum, inv) => sum + inv.total, 0) + overdueInvoices.reduce((sum, inv) => sum + inv.total, 0);

  const periods = ['Today', 'This Week', 'This Month', 'This Year', 'All Time'];

  const recentInvoices = invoices.slice(0, 4); // Reduced to fit better

  const formatCurrency = (amount: number) => {
    return `${currencySymbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <>
      <SEO
        title="Dashboard"
        description="View your freelance business analytics, revenue, and recent activity on the FreelancePay dashboard."
        keywords={["dashboard", "analytics", "freelance", "revenue", "FreelancePay"]}
      />
      <div className="h-screen bg-gradient-to-br from-gray-50 via-white to-green-50 overflow-hidden">
        {/* Compact Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-3">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome back to your invoice dashboard</p>
            </div>
            <div className="flex items-center space-x-3">
              {/* Compact Time Period Selector */}
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                {periods.map((period) => (
                  <option key={period} value={period}>{period}</option>
                ))}
              </select>
              <button 
                onClick={() => navigate('/app/invoices/new')}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg transform hover:scale-105 font-semibold text-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Invoice
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 h-full overflow-y-auto">
          {/* Compact Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Total Revenue */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 font-medium">TOTAL REVENUE</div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(totalRevenue)}</div>
                  <div className="text-xs text-gray-500">{invoices.length} Invoices</div>
                </div>
              </div>
            </div>

            {/* Paid Amount */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 font-medium">PAID AMOUNT</div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(paidAmount)}</div>
                  <div className="text-xs text-gray-500">{paidInvoices.length} Paid</div>
                </div>
              </div>
            </div>

            {/* Unpaid Amount */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 font-medium">UNPAID AMOUNT</div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(unpaidAmount)}</div>
                  <div className="text-xs text-gray-500">{pendingInvoices.length + overdueInvoices.length} Unpaid</div>
                </div>
              </div>
            </div>

            {/* Average Invoice */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart className="h-5 w-5 text-purple-600" />
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 font-medium">AVG INVOICE</div>
                  <div className="text-lg font-bold text-gray-900">
                    {formatCurrency(invoices.length > 0 ? totalRevenue / invoices.length : 0)}
                  </div>
                  <div className="text-xs text-gray-500">Per Invoice</div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid - Optimized for single view */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-96">
            {/* Recent Invoices - Takes more space */}
            <div className="lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-100 flex flex-col">
              <div className="p-4 border-b border-gray-100 flex-shrink-0">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Invoices</h3>
                  <button 
                    onClick={() => navigate('/app/invoices')}
                    className="text-green-600 hover:text-green-700 text-sm font-medium transition-colors flex items-center"
                  >
                    View All
                    <ArrowUpRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
              <div className="p-4 flex-1 overflow-y-auto">
                {recentInvoices.length > 0 ? (
                  <div className="space-y-3">
                    {recentInvoices.map((invoice) => (
                      <div 
                        key={invoice.id} 
                        className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg hover:from-green-50 hover:to-emerald-50 transition-all cursor-pointer group border border-gray-100 hover:border-green-200"
                        onClick={() => navigate(`/app/invoices/${invoice.id}`)}
                      >
                        <div className="flex items-center">
                          <div className="p-2 bg-green-100 rounded-lg mr-3 group-hover:bg-green-200 transition-colors">
                            <FileText className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">#{invoice.invoiceNumber}</p>
                            <p className="text-xs text-gray-600">{invoice.client.name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900 text-sm">{formatCurrency(invoice.total)}</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                            invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                            invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No invoices found</p>
                    <p className="text-sm text-gray-400 mb-4">Create Your First Invoice</p>
                    <button 
                      onClick={() => navigate('/app/invoices/new')}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Invoice
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Compact Right Sidebar */}
            <div className="space-y-4 flex flex-col">
              {/* Quick Actions */}
              <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg p-4 text-white flex-shrink-0">
                <h3 className="text-sm font-semibold mb-2">Quick Actions</h3>
                <div className="space-y-2">
                  <button 
                    onClick={() => navigate('/app/invoices/new')}
                    className="w-full flex items-center justify-center px-3 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all backdrop-blur-sm font-medium text-sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Invoice
                  </button>
                  <button 
                    onClick={() => navigate('/app/clients')}
                    className="w-full flex items-center justify-center px-3 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all backdrop-blur-sm font-medium text-sm"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Add Client
                  </button>
                </div>
              </div>

              {/* Compact Summary Stats */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Summary</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="font-medium text-gray-900 text-sm">Total</span>
                    </div>
                    <span className="text-sm font-bold text-blue-600">{invoices.length}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center">
                      <TrendingUp className="h-4 w-4 text-green-600 mr-2" />
                      <span className="font-medium text-gray-900 text-sm">Paid</span>
                    </div>
                    <span className="text-sm font-bold text-green-600">{paidInvoices.length}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 text-orange-600 mr-2" />
                      <span className="font-medium text-gray-900 text-sm">Pending</span>
                    </div>
                    <span className="text-sm font-bold text-orange-600">{pendingInvoices.length}</span>
                  </div>

                  <div className="pt-3 border-t border-gray-200">
                    <button 
                      onClick={() => navigate('/app/invoices')}
                      className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                    >
                      Manage Invoices
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function getCurrencySymbol(currency: string): string {
  const symbols: { [key: string]: string } = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'QAR': 'QAR ',
    'AED': 'AED ',
    'SAR': 'SAR ',
    'INR': '₹',
    'JPY': '¥',
    'CNY': '¥',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'CHF ',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'CZK': 'Kč',
    'HUF': 'Ft',
    'RUB': '₽',
    'BRL': 'R$',
    'MXN': '$',
    'ZAR': 'R',
    'KRW': '₩',
    'SGD': 'S$',
    'HKD': 'HK$',
    'NZD': 'NZ$',
    'THB': '฿',
    'MYR': 'RM',
    'PHP': '₱',
    'IDR': 'Rp',
    'VND': '₫',
    'TRY': '₺',
    'ILS': '₪',
    'EGP': 'E£',
    'NGN': '₦',
    'KES': 'KSh',
    'GHS': 'GH₵',
    'MAD': 'MAD ',
    'TND': 'TND ',
    'DZD': 'DZD ',
    'LBP': 'L£',
    'JOD': 'JD',
    'KWD': 'KD',
    'BHD': 'BD',
    'OMR': 'OMR ',
  };
  return symbols[currency] || currency + ' ';
}